import React from 'react'
import {
  Alert,
  Button,
  Cascader,
  Form,
  Input,
  Select,
  Switch,
  Table,
  Tooltip,
  Typography
} from 'antd'
import {
  CaretDownFilled,
  CheckCircleFilled,
  InfoCircleOutlined,
  PlayCircleOutlined
} from '@ant-design/icons'
import { AxiosError } from 'axios'
import { ColumnsType } from 'antd/es/table'
import { productApi } from 'api/product'
import { sellerApi } from 'api/seller'
import { IParsedCategory } from 'models/product'
import {
  IMoySkladLinkerItem,
  IMoySkladSaveLinkerItem,
  IMoySkladSyncStatusResponse,
  IStockConfirmationStatus,
  TVariantSeller
} from 'models/seller'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { useStoreHiddenSync } from 'shared/hooks/useStoreHiddenSync'
import { StockConfirmationStatus } from 'components/sellers/StockConfirmationStatus/StockConfirmationStatus'
import { IntegrationActions } from 'components/sellers/IntegrationActions/IntegrationActions'
import { SectionTitle } from 'components/ui'
import { SyncLaunchModal } from './ui/SyncLaunchModal'
import { SyncStatusPanel } from './ui/SyncStatusPanel'
import {
  getSyncStatusMeta,
  IN_PROGRESS_STATUS,
  SYNC_STATUS_POLL_INTERVAL
} from './utils/syncStatus'
import styles from './SellerApiMoySklad.module.scss'

interface ISellerApiMoySkladProps {
  type: TVariantSeller
  sellerId: string
}

interface ITokenFormData {
  token: string
}

interface ILinkerTableItem extends IMoySkladLinkerItem {
  children?: ILinkerTableItem[]
  depth: number
}

interface ILinkerDraft {
  category_id: string | null
  subCategory_id: string | null
  doNotDisplay: boolean
}

interface IMoySkladApiErrorResponse {
  statusCode?: number
  message?: string
  errors?: Array<{
    error?: string
    code?: number
  }>
}

const LINKERS_SKIP = '0'
const LINKERS_LIMIT = '1000'
const MOYSKLAD_INSTRUCTION_URL =
  'https://bookstack-production-3d81.up.railway.app/books/horsesmart-dlia-prodavca-sinxronizaciia-tovarov/page/instrukciia-po-sinxronizacii-tovarov-iz-moisklad-v-horsesmart'
const SYNC_DISABLED_HINT =
  'Синхронизация с МойСклад отключена. Включите её переключателем выше, чтобы запустить обмен.'
const SALE_FIELD_REQUIRED_HINT =
  'Сначала выберите ценовое поле МойСклад — без него синхронизацию запустить нельзя.'
const CONNECTION_CHECK_SUCCESS =
  'Проверка подключения успешна. Установите ценовое поле и соответствия категорий'
const MOYSKLAD_VIDEO_INSTRUCTION_URL =
  'https://rutube.ru/video/private/4c2ffdb073efb2379733b7b455a20da0/?p=V73Ez22aHG4TCnCmvYcWog'
const EMPTY_GROUP_LABEL = 'Без группы'

// Из двух линкеров одного пути главный тот, на котором висят товары: именно его
// настройки применяются при синхронизации. При равенстве берём заведённый по
// группе МойСклада — только у него есть externalId, по нему строится дерево
const isBetterLinker = (
  candidate: IMoySkladLinkerItem,
  current: IMoySkladLinkerItem
) => {
  const candidateProducts = candidate.product_ids?.length ?? 0
  const currentProducts = current.product_ids?.length ?? 0

  if (candidateProducts !== currentProducts) {
    return candidateProducts > currentProducts
  }

  return Boolean(candidate.externalId) && !current.externalId
}

export const SellerApiMoySklad: React.FC<ISellerApiMoySkladProps> = ({
  sellerId
}) => {
  const isMobile = useIsMobile()
  const syncStoreHidden = useStoreHiddenSync()

  const linkersRequestIdRef = React.useRef(0)

  const [isLoaded, setIsLoaded] = React.useState(false)
  const [isLoadingLinkers, setIsLoadingLinkers] = React.useState(false)
  const [isCheckingToken, setIsCheckingToken] = React.useState(false)
  const [isConnectionChecked, setIsConnectionChecked] = React.useState(false)
  const [isSavingSaleField, setIsSavingSaleField] = React.useState(false)
  const [isRefreshingGroups, setIsRefreshingGroups] = React.useState(false)
  const [isSavingAndSyncing, setIsSavingAndSyncing] = React.useState(false)
  const [isSyncLaunchModalOpen, setIsSyncLaunchModalOpen] = React.useState(false)
  const [syncActionError, setSyncActionError] = React.useState('')
  const [syncStatus, setSyncStatus] = React.useState<IMoySkladSyncStatusResponse | null>(null)
  const [stockStatus, setStockStatus] = React.useState<IStockConfirmationStatus | null>(null)
  const [isStockStatusLoading, setIsStockStatusLoading] = React.useState(true)
  const [isSyncToggling, setIsSyncToggling] = React.useState(false)

  const [hasSavedToken, setHasSavedToken] = React.useState(false)

  const [moySkladLinkers, setMoySkladLinkers] = React.useState<IMoySkladLinkerItem[]>([])
  const [saleFields, setSaleFields] = React.useState<string[]>([])
  const [selectedSaleField, setSelectedSaleField] = React.useState<string | undefined>()
  const [categories, setCategories] = React.useState<IParsedCategory[]>([])
  const [linkerDraftMap, setLinkerDraftMap] = React.useState<Record<string, ILinkerDraft>>({})
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<React.Key[]>([])

  const [tokenForm] = Form.useForm<ITokenFormData>()
  const syncStatusRef = React.useRef<IMoySkladSyncStatusResponse | null>(null)

  const getSyncActionErrorMessage = React.useCallback((e: unknown): string | null => {
    const error = e as AxiosError<IMoySkladApiErrorResponse>
    const firstErrorMessage = error.response?.data?.errors?.[0]?.error?.trim()
    if (firstErrorMessage) {
      return firstErrorMessage
    }

    const responseMessage = error.response?.data?.message?.trim()
    if (responseMessage) {
      return responseMessage
    }

    return null
  }, [])

  const syncStatusMeta = React.useMemo(
    () => getSyncStatusMeta(syncStatus),
    [syncStatus]
  )

  // Токен у продавца есть — интеграция подключена. Статус остатков тот же признак
  // отдаёт полем, но оно появилось позже, и на старом API его в ответе нет
  const isMoySkladConnected = Boolean(
    stockStatus?.isMoySkladConnected || hasSavedToken
  )
  // Отсутствие флага в ответе читаем как включённую синхронизацию — так же, как бэкенд
  const isMoySkladSyncEnabled = stockStatus?.isMoySkladSyncEnabled !== false
  const isSyncDisabledBySeller = isMoySkladConnected && !isMoySkladSyncEnabled

  const isSaleFieldMissing = !selectedSaleField
  const isSyncBlocked = isSyncDisabledBySeller || isSaleFieldMissing
  const syncDisabledHint =
    (isSyncDisabledBySeller && SYNC_DISABLED_HINT) ||
    (isSaleFieldMissing && SALE_FIELD_REQUIRED_HINT) ||
    ''

  const setDraftMapFromLinkers = React.useCallback((linkers: IMoySkladLinkerItem[]) => {
    const map: Record<string, ILinkerDraft> = {}
    linkers.forEach((linker) => {
      map[linker._id] = {
        category_id: linker.category_id ?? null,
        subCategory_id: linker.subCategory_id ?? null,
        doNotDisplay: Boolean(linker.doNotDisplay)
      }
    })
    setLinkerDraftMap(map)
  }, [])

  const getMoySkladLinkers = React.useCallback(async () => {
    const requestId = ++linkersRequestIdRef.current
    try {
      setIsLoadingLinkers(true)
      const response = await sellerApi.moySkladGetLinkers({
        seller_id: sellerId,
        skip: LINKERS_SKIP,
        limit: LINKERS_LIMIT
      })
      if (requestId !== linkersRequestIdRef.current) {
        return
      }
      const linkers = response.data.data ?? []
      setMoySkladLinkers(linkers)
      setDraftMapFromLinkers(linkers)
    } catch (e) {
      showServerError(e)
    } finally {
      if (requestId === linkersRequestIdRef.current) {
        setIsLoadingLinkers(false)
      }
    }
  }, [sellerId, setDraftMapFromLinkers])

  const applySyncStatus = React.useCallback(
    (nextStatus: IMoySkladSyncStatusResponse | null) => {
      setSyncStatus(nextStatus)
      syncStatusRef.current = nextStatus
    },
    []
  )

  const getSyncStatus = React.useCallback(async () => {
    try {
      const response = await sellerApi.moySkladGetSyncStatus({
        seller_id: sellerId
      })
      applySyncStatus(response.data ?? null)
    } catch (e) {
      showServerError(e)
    }
  }, [applySyncStatus, sellerId])

  const getCategories = React.useCallback(async () => {
    try {
      const response = await productApi.getCategories({
        parsed: 'true'
      })
      setCategories(response.data as IParsedCategory[])
    } catch (e) {
      showServerError(e)
    }
  }, [])

  const getStockConfirmationStatus = React.useCallback(async () => {
    try {
      setIsStockStatusLoading(true)
      const response = await sellerApi.getStockConfirmationStatus(sellerId)
      setStockStatus(response.data)
      syncStoreHidden(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsStockStatusLoading(false)
    }
  }, [sellerId, syncStoreHidden])

  const onToggleSync = async (enabled: boolean) => {
    try {
      setIsSyncToggling(true)
      const response = await sellerApi.setMoySkladSyncEnabled(enabled, sellerId)
      setStockStatus(response.data)
      syncStoreHidden(response.data)
      showMessage(
        'success',
        enabled ? 'Синхронизация включена' : 'Синхронизация отключена',
        enabled
          ? ''
          : 'Обновляйте остатки вручную и подтверждайте их актуальность в разделе «Товары».'
      )
      if (enabled) {
        await getSyncStatus()
      }
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSyncToggling(false)
    }
  }

  const getSellerMoySkladSettings = React.useCallback(async () => {
    const response = await sellerApi.getSeller(sellerId)
    const moySkladSettings = response.data?.moy_sklad
    tokenForm.setFieldValue('token', moySkladSettings?.api_token ?? '')
    setHasSavedToken(Boolean(moySkladSettings?.api_token))
    setSaleFields(moySkladSettings?.sale_fields ?? [])
    setSelectedSaleField(moySkladSettings?.select_sale_field || undefined)
  }, [sellerId, tokenForm])

  React.useEffect(() => {
    const init = async () => {
      try {
        await getSellerMoySkladSettings()
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoaded(true)
      }
    }

    init()
    getCategories()
    getMoySkladLinkers()
    getSyncStatus()
    getStockConfirmationStatus()
  }, [
    sellerId,
    getCategories,
    getMoySkladLinkers,
    getSyncStatus,
    getStockConfirmationStatus,
    getSellerMoySkladSettings
  ])

  React.useEffect(() => {
    if (syncStatus?.status !== IN_PROGRESS_STATUS) {
      return
    }

    const intervalId = window.setInterval(() => {
      getSyncStatus()
    }, SYNC_STATUS_POLL_INTERVAL)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [getSyncStatus, syncStatus?.status])

  // Старая синхронизация заводила линкеры по имени группы, «Обновить список
  // групп» — по externalId, поэтому на один путь их может быть два. В таблице
  // показываем один, а при сохранении дублю уходят те же настройки
  const visibleLinkers = React.useMemo<IMoySkladLinkerItem[]>(() => {
    const bestByPath = new Map<string, IMoySkladLinkerItem>()

    moySkladLinkers.forEach((linker) => {
      const pathName = linker.pathName ?? ''
      const current = bestByPath.get(pathName)

      if (!current || isBetterLinker(linker, current)) {
        bestByPath.set(pathName, linker)
      }
    })

    return moySkladLinkers.filter(
      (linker) => bestByPath.get(linker.pathName ?? '') === linker
    )
  }, [moySkladLinkers])

  const effectiveLinkerIdMap = React.useMemo<Record<string, string>>(() => {
    const idByPath = new Map<string, string>(
      visibleLinkers.map((linker) => [linker.pathName ?? '', linker._id])
    )

    return moySkladLinkers.reduce<Record<string, string>>((map, linker) => {
      map[linker._id] = idByPath.get(linker.pathName ?? '') ?? linker._id
      return map
    }, {})
  }, [moySkladLinkers, visibleLinkers])

  const linkersTreeData = React.useMemo<ILinkerTableItem[]>(() => {
    const nodesById = new Map<string, ILinkerTableItem>()
    const nodeIdByExternalId = new Map<string, string>()
    const roots: ILinkerTableItem[] = []

    visibleLinkers.forEach((linker) => {
      if (nodesById.has(linker._id)) {
        return
      }

      nodesById.set(linker._id, { ...linker, children: [], depth: 0 })
      if (linker.externalId && !nodeIdByExternalId.has(linker.externalId)) {
        nodeIdByExternalId.set(linker.externalId, linker._id)
      }
    })

    visibleLinkers.forEach((linker) => {
      const currentNode = nodesById.get(linker._id)
      if (!currentNode) {
        return
      }

      if (!linker.parentExternalId) {
        roots.push(currentNode)
        return
      }

      const parentId = nodeIdByExternalId.get(linker.parentExternalId)
      if (!parentId || parentId === linker._id) {
        roots.push(currentNode)
        return
      }

      const parentNode = nodesById.get(parentId)
      if (!parentNode) {
        roots.push(currentNode)
        return
      }

      parentNode.children = [...(parentNode.children ?? []), currentNode]
    })

    const setDepth = (nodes: ILinkerTableItem[], depth: number) => {
      nodes.forEach((node) => {
        node.depth = depth
        setDepth(node.children ?? [], depth + 1)
      })
    }
    setDepth(roots, 0)

    return roots
  }, [visibleLinkers])

  const updateLinkerDraft = React.useCallback(
    (linkerId: string, patch: Partial<ILinkerDraft>) => {
      setLinkerDraftMap((prev) => ({
        ...prev,
        [linkerId]: {
          ...prev[linkerId],
          ...patch
        }
      }))
    },
    []
  )

  const onCheckToken = async () => {
    try {
      const values = await tokenForm.validateFields()
      setIsCheckingToken(true)
      setIsConnectionChecked(false)
      // Сохранение токена на стороне интеграции ходит в МойСклад и заодно тянет ценовые поля
      await sellerApi.moySkladSaveToken({
        token: values.token.trim(),
        seller_id: sellerId
      })
      await getSellerMoySkladSettings()
      setSyncActionError('')
      setIsConnectionChecked(true)
    } catch (e) {
      const error = e as AxiosError<{ message?: string }>
      const errorMessage = error.response?.data?.message
      // 400 от интеграции на этом запросе всегда про токен: он невалиден,
      // протух или выдан сотрудником без прав администратора
      const isTokenValidationError = Boolean(
        error.response?.status === 400 && errorMessage
      )

      if (isTokenValidationError && errorMessage) {
        tokenForm.setFields([
          {
            name: 'token',
            errors: [errorMessage]
          }
        ])
        return
      }

      if (!(e as { errorFields?: unknown[] })?.errorFields) {
        showServerError(e)
      }
    } finally {
      setIsCheckingToken(false)
    }
  }

  const onSelectSaleField = async (value: string) => {
    try {
      setIsSavingSaleField(true)
      await sellerApi.moySkladSelectSaleField(sellerId, {
        select_sale_field: value
      })
      setSelectedSaleField(value)
      showMessage('success', 'Ценовое поле сохранено', '')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSavingSaleField(false)
    }
  }

  const onRefreshGroups = async () => {
    try {
      setSyncActionError('')
      setIsRefreshingGroups(true)
      await sellerApi.moySkladRefreshGroups({
        seller_id: sellerId
      })
      await getSellerMoySkladSettings()
      await getMoySkladLinkers()
      showMessage('success', 'Список групп обновлен', '')
    } catch (e) {
      const syncActionErrorMessage = getSyncActionErrorMessage(e)
      if (syncActionErrorMessage) {
        setSyncActionError(syncActionErrorMessage)
        return
      }
      showServerError(e)
    } finally {
      setIsRefreshingGroups(false)
    }
  }

  const getPreparedLinkersForSave = React.useCallback((): IMoySkladSaveLinkerItem[] | null => {
    for (const linker of visibleLinkers) {
      const draft = linkerDraftMap[linker._id]
      if (!draft) {
        continue
      }

      // Категорию получают товары, а не сама группа, поэтому от групп без
      // своих товаров (например, верхней «Товары») сопоставления не требуем
      const hasOwnProducts = (linker.product_ids?.length ?? 0) > 0

      if (
        hasOwnProducts &&
        !draft.doNotDisplay &&
        (!draft.category_id || !draft.subCategory_id)
      ) {
        showMessage(
          'warning',
          'Заполните соответствие категорий',
          `Укажите категорию и подкатегорию для группы «${linker.pathName || EMPTY_GROUP_LABEL}» или выключите её загрузку.`
        )
        return null
      }
    }

    return moySkladLinkers.map((linker) => {
      const draft = linkerDraftMap[effectiveLinkerIdMap[linker._id] ?? linker._id]
      return {
        linker_id: linker._id,
        category_id: draft?.category_id ?? '',
        subCategory_id: draft?.subCategory_id ?? '',
        doNotDisplay: Boolean(draft?.doNotDisplay)
      }
    })
  }, [moySkladLinkers, visibleLinkers, effectiveLinkerIdMap, linkerDraftMap])

  const openSyncLaunchModal = () => {
    bodyScrollLock.enable()
    setIsSyncLaunchModalOpen(true)
  }

  const closeSyncLaunchModal = () => {
    bodyScrollLock.disable()
    setIsSyncLaunchModalOpen(false)
  }

  const confirmSyncLaunch = async () => {
    const updateData = getPreparedLinkersForSave()
    if (!updateData) {
      return
    }

    try {
      setSyncActionError('')
      setIsSavingAndSyncing(true)
      await sellerApi.connectMoySkladCategoriesV2({
        updateData,
        seller_id: sellerId
      })
      await sellerApi.moySkladSaveAndSync({
        seller_id: sellerId
      })
      applySyncStatus({
        status: 'В процессе',
        runId: syncStatusRef.current?.runId ?? '__pending__',
        processed: syncStatusRef.current?.processed ?? syncStatusRef.current?.totalCount ?? 0,
        draftCount: syncStatusRef.current?.draftCount ?? syncStatusRef.current?.notPublishedCount ?? 0,
        disabledCount: syncStatusRef.current?.disabledCount ?? 0,
        reportUrl: '',
        completedAt: null
      })
      closeSyncLaunchModal()
      await getSyncStatus()
    } catch (e) {
      const error = e as AxiosError<{ message?: string }>
      if (error.response?.status === 403) {
        showMessage('error', 'Недостаточно прав для запуска синхронизации', '')
        return
      }
      const syncActionErrorMessage = getSyncActionErrorMessage(e)
      if (syncActionErrorMessage) {
        setSyncActionError(syncActionErrorMessage)
        return
      }
      showServerError(e)
    } finally {
      setIsSavingAndSyncing(false)
    }
  }

  const columns: ColumnsType<ILinkerTableItem> = React.useMemo(
    () => [
      {
        title: 'Группа (МойСклад)',
        dataIndex: 'pathName',
        width: '40%',
        render: (_, record) => {
          const { _id, pathName, children } = record
          const hasChildren = Boolean(children?.length)
          const isExpanded = expandedRowKeys.includes(_id)

          return (
            <div className={styles.pathCell}>
              {hasChildren ? (
                <button
                  type='button'
                  onClick={() => {
                    setExpandedRowKeys((prev) =>
                      prev.includes(_id)
                        ? prev.filter((key) => key !== _id)
                        : [...prev, _id]
                    )
                  }}
                  className={styles.expandButton}
                  aria-label={isExpanded ? 'Свернуть группу' : 'Развернуть группу'}
                >
                  <CaretDownFilled
                    className={`${styles.expandIcon} ${isExpanded ? styles.expandIconExpanded : ''}`}
                  />
                </button>
              ) : null}
              <Typography.Text
                className={styles.pathName}
                title={pathName || EMPTY_GROUP_LABEL}
              >
                {pathName || EMPTY_GROUP_LABEL}
              </Typography.Text>
            </div>
          )
        }
      },
      {
        title: 'Загружать',
        dataIndex: 'doNotDisplay',
        width: '15%',
        render: (_, { _id }) => {
          const isEnabled = !(linkerDraftMap[_id]?.doNotDisplay ?? false)
          return (
            <div className={styles.checkboxCell}>
              <Switch
                checked={isEnabled}
                onChange={(checked) => updateLinkerDraft(_id, { doNotDisplay: !checked })}
              />
            </div>
          )
        }
      },
      {
        title: 'Категория HorseSmart',
        dataIndex: 'categories',
        width: '45%',
        render: (_, { _id }) => {
          const draft = linkerDraftMap[_id]
          const value = draft?.category_id && draft?.subCategory_id
            ? [draft.category_id, draft.subCategory_id]
            : undefined

          return (
            <Cascader
              className={styles.categoryCascader}
              value={value}
              showSearch
              allowClear
              placeholder='Выбор категории'
              rootClassName='myCascader'
              popupClassName={styles.categorySelect}
              options={categories}
              fieldNames={{
                label: 'title'
              }}
              expandTrigger='click'
              onChange={(nextValue) => {
                updateLinkerDraft(_id, {
                  category_id: nextValue?.[0] ? String(nextValue[0]) : null,
                  subCategory_id: nextValue?.[1] ? String(nextValue[1]) : null
                })
              }}
            />
          )
        }
      }
    ],
    [categories, expandedRowKeys, linkerDraftMap, updateLinkerDraft]
  )

  // Рендеры мобильных колонок берём из десктопных, чтобы правки не расходились
  const mobileColumns: ColumnsType<ILinkerTableItem> = React.useMemo(() => {
    const [pathColumn, doNotDisplayColumn, categoriesColumn] = columns

    return [
      {
        title: 'Группа (МойСклад)',
        dataIndex: 'pathName',
        render: (value, record, index) => (
          <div className={styles.mobileCell}>
            {pathColumn?.render?.(value, record, index) as React.ReactNode}
            <div className={styles.mobileCheckboxRow}>
              {doNotDisplayColumn?.render?.(value, record, index) as React.ReactNode}
              <Typography.Text className={styles.mobileCheckboxLabel}>
                Загружать
              </Typography.Text>
            </div>
            {categoriesColumn?.render?.(value, record, index) as React.ReactNode}
          </div>
        )
      }
    ]
  }, [columns])

  if (!isLoaded) {
    return null
  }

  return (
    <div className={styles.root}>
      <SyncLaunchModal
        open={isSyncLaunchModalOpen}
        isSubmitting={isSavingAndSyncing}
        onCancel={closeSyncLaunchModal}
        onConfirm={confirmSyncLaunch}
      />
      <div className={styles.content}>
      <SectionTitle level={2} className={styles.title}>
        Синхронизация мой склад
      </SectionTitle>
      <StockConfirmationStatus
        sellerId={sellerId}
        status={stockStatus}
        isLoading={isStockStatusLoading}
      />
      <SyncStatusPanel
        meta={syncStatusMeta}
        isConnected={isMoySkladConnected}
        isEnabled={isMoySkladSyncEnabled}
        isToggling={isSyncToggling || isStockStatusLoading}
        onToggle={onToggleSync}
      />
      <Form form={tokenForm} name='moySkladToken' size='large' className={styles.form}>
        <div style={{ flexGrow: 1 }}>
          <Form.Item
            name='token'
            className={styles.input}
            rules={[
              {
                required: true,
                message: 'Укажите токен'
              },
              {
                whitespace: true,
                message: 'Укажите токен'
              }
            ]}
            hasFeedback
          >
            <Input
              placeholder='40a8cedcc57b2161430935fd558ad51a434a30ec'
              onChange={() => setIsConnectionChecked(false)}
            />
          </Form.Item>
          {isConnectionChecked && (
            <div className={styles.connectionChecked}>
              <CheckCircleFilled />
              <span>{CONNECTION_CHECK_SUCCESS}</span>
            </div>
          )}
          <Alert
            type='info'
            showIcon
            icon={<InfoCircleOutlined />}
            message='При смене API токена в системе МойСклад синхронизация перестает работать!'
            className={styles.tokenAlert}
          />
        </div>
        <Button type='default' onClick={onCheckToken} loading={isCheckingToken}>
          Проверить
        </Button>
      </Form>
      <Typography.Text className={styles.info}>
        Токен может быть создан на{' '}
        <Typography.Link
          href='https://online.moysklad.ru/app/#token'
          target='_blank'
          className={styles.linkToMoySklad}
        >
          странице настроек Мой склад
        </Typography.Link>
      </Typography.Text>

      <div className={styles.saleFieldSection}>
        <div className={styles.saleFieldText}>
          <Typography.Title level={4} className={styles.saleFieldTitle}>
            Ценовое поле МойСклад
          </Typography.Title>
          <Typography.Text className={styles.saleFieldDescription}>
            Выберите поле цены, которое будет использоваться при синхронизации товаров.
          </Typography.Text>
        </div>
        <Select
          className={styles.saleFieldSelect}
          placeholder='Выберите ценовое поле'
          value={selectedSaleField}
          loading={isSavingSaleField}
          disabled={!saleFields.length || isSavingSaleField}
          options={saleFields.map((field) => ({
            value: field,
            label: field
          }))}
          onChange={onSelectSaleField}
        />
      </div>

      <Typography.Title level={2} className={styles.moySkladCategoriesTitle}>
        Установите соответствие категорий
      </Typography.Title>
      <div className={styles.categoriesHeader}>
        <Typography.Text className={styles.categoriesDescription}>
          Выберите группы и подгруппы, которые нужно синхронизировать.
        </Typography.Text>
        <div className={styles.instructionsActions}>
          <Typography.Link
            href={MOYSKLAD_INSTRUCTION_URL}
            target='_blank'
            rel='noreferrer'
          >
            Инструкция по синхронизации
          </Typography.Link>
          <IntegrationActions
            actions={[
              {
                label: 'ВИДЕО-ИНСТРУКЦИЯ',
                url: MOYSKLAD_VIDEO_INSTRUCTION_URL,
                icon: <PlayCircleOutlined />
              }
            ]}
          />
        </div>
      </div>

      <Table
        loading={isLoadingLinkers}
        size={isMobile ? 'small' : undefined}
        tableLayout='fixed'
        columns={isMobile ? mobileColumns : columns}
        dataSource={linkersTreeData}
        rowKey={(item) => item._id}
        rowClassName={({ depth }) => (depth % 2 ? styles.nestedRow : '')}
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          hideOnSinglePage: true
        }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (nextExpandedRows) => setExpandedRowKeys([...nextExpandedRows]),
          expandIcon: () => null
        }}
        className={styles.linkersTable}
      />

      <Typography.Text className={styles.footerDescription}>
        После сохранения все синхронизированные товары будут привязаны к выбранным Категориям/Подкатегориям HorseSmart.
        Если загрузка Группы/Подгруппы выключена, её товары в HorseSmart не загружаются, а уже загруженные переходят в статус «Отключен».
      </Typography.Text>

      <div className={styles.linkerSubmitButtonWrapper}>
        <Button
          htmlType='button'
          size='large'
          onClick={onRefreshGroups}
          loading={isRefreshingGroups}
          disabled={isSyncDisabledBySeller}
        >
          Обновить список групп
        </Button>
        <Tooltip title={syncDisabledHint}>
          <span className={styles.syncButtonWrapper}>
            <Button
              htmlType='button'
              type='primary'
              size='large'
              onClick={openSyncLaunchModal}
              loading={isSavingAndSyncing}
              disabled={isSyncBlocked}
            >
              Сохранить и синхронизировать
            </Button>
          </span>
        </Tooltip>
      </div>
      {syncActionError && (
        <Typography.Text className={styles.syncActionError}>
          {syncActionError}
        </Typography.Text>
      )}
      </div>
    </div>
  )
}
